import React, { useMemo } from 'react';
import { Empty, Spin } from 'antd';
import Icon from '@ant-design/icons';
import Highlighter from 'react-highlight-words';

import { Operator } from '@src/emp-3.0/types';
import { formatOperator, getOperatorOptions } from '@src/emp-3.0/utils';
import { useGetDimensionsList } from '@api/common/queryHooks';
import { IDimensionMappedEvent } from '@src/emp-3.0/types/mapped-event';
import MappingRowSvg from '@icons/emp/mapping-builder-lined.svg';

import {
  ANDTag,
  ANDLine,
  Body,
  Container,
  EventNameValueWrapper,
  EventNameWrapper,
  Header,
  MappingRules,
  MappingRulesContainer,
  Name,
  ORTag,
  RulesBuilderContainer,
  Value,
} from './styled';

interface IProps {
  loading?: boolean;
  data?: IDimensionMappedEvent;
}

function getLabelByValue(value: Operator): string | undefined {
  const option = getOperatorOptions().operatorOptions.find((option) => option.value === value);
  return option ? option.label : undefined;
}

export const MappingRulesRender: React.FC<{
  rules: any[];
  searchText?: string;
}> = ({ rules, searchText = '' }) => {
  const { data: dimensionList } = useGetDimensionsList();

  const dimensionNameMap = useMemo(() => {
    const nameMap = new Map();
    (dimensionList?.data ?? []).forEach((item) => {
      nameMap[item.dimension_name] = item.display_name;
    });
    return nameMap;
  }, [dimensionList?.data]);

  if (!Array.isArray(rules)) {
    return null;
  }

  return (
    <div
      style={{ maxHeight: '600px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
    >
      {/* <Value>Mapping Rules</Value> */}
      <RulesBuilderContainer>
        {rules?.map((andRules, andIndex) => {
          return andRules.map((orRule, orIndex) => {
            const rulesLabel =
              andIndex === 0 && orIndex === 0 ? (
                ''
              ) : orIndex === 0 ? (
                <ANDTag as="div" style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
                  AND
                </ANDTag>
              ) : (
                <ORTag as="div" style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
                  OR
                </ORTag>
              );
            return (
              <div key={`${orRule.value}_${andIndex}_${orIndex}`}>
                <EventNameWrapper>
                  {rulesLabel && <span style={{ padding: '8px' }}>{rulesLabel}</span>}
                  <span style={{ padding: '8px', fontWeight: 500 }}>Raw Event :</span>
                  <span style={{ padding: '8px 12px 8px 4px', color: 'rgba(0, 0, 0, 0.88)' }}>
                    <Highlighter
                      autoEscape
                      textToHighlight={`${orRule.value.join()}`}
                      highlightStyle={{
                        mixBlendMode: 'darken',
                        fontWeight: 500,
                      }}
                      searchWords={[searchText]}
                    />
                  </span>
                </EventNameWrapper>
                {orRule.mapping_rules.map((andMappingRules, andMappingRulesIndex) => {
                  return (
                    <MappingRulesContainer key={`andMappingRules_${andMappingRulesIndex}`}>
                      {andMappingRules.map((orMappingRule, orMappingRuleIndex) => {
                        const mappingRulesLabel =
                          orMappingRuleIndex === 0 ? (
                            <span
                              style={{
                                width: '28px',
                                display: 'flex',
                                flexShrink: 0,
                                justifyContent:
                                  andMappingRulesIndex !== 0 && orMappingRuleIndex === 0
                                    ? 'normal'
                                    : 'end',
                              }}
                            ></span>
                          ) : (
                            <span
                              style={{
                                width: '28px',
                                display: 'flex',
                                flexShrink: 0,
                                justifyContent:
                                  andMappingRulesIndex !== 0 && orMappingRuleIndex === 0
                                    ? 'normal'
                                    : 'end',
                              }}
                            >
                              <ORTag>OR</ORTag>
                            </span>
                          );
                        return (
                          <>
                            {andMappingRulesIndex !== 0 && orMappingRuleIndex === 0 && <ANDLine />}
                            <MappingRules key={`orMappingRule_${orMappingRuleIndex}`}>
                              {mappingRulesLabel}
                              <span style={{ color: 'rgba(0, 0, 0, 0.88)' }}>
                                {orMappingRule.tag_key.value.join(',')}
                              </span>
                              <span>{formatOperator(orMappingRule.tag_value.op)}</span>
                              <span
                                style={{
                                  color: 'rgba(0, 0, 0, 0.88)',
                                  textAlign: 'left',
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {orMappingRule.tag_value.value.join(',')}
                              </span>
                            </MappingRules>
                          </>
                        );
                      })}
                    </MappingRulesContainer>
                  );
                })}
                {orRule.metadata.map((andMetadata, andMetadataIndex) => {
                  const metadataKey = andMetadata[0]?.key;
                  const metadataName = dimensionNameMap[metadataKey] ?? metadataKey;

                  return (
                    <div key={`andMetadata_${andMetadataIndex}`}>
                      <EventNameWrapper as="div" style={{ border: 'none' }}>
                        <ANDTag as="div" style={{ margin: '0 8px' }}>
                          AND
                        </ANDTag>
                        <span style={{ padding: '8px 8px 8px 0', fontWeight: 500 }}>Metadata</span>
                        <span
                          style={{
                            padding: '8px 12px 8px 4px',
                            color: 'rgba(0, 0, 0, 0.88)',
                          }}
                        >
                          {metadataName}
                        </span>
                      </EventNameWrapper>
                      <MappingRulesContainer>
                        {andMetadata.map((orMetadata, orMetadataIndex) => {
                          return (
                            <MappingRules key={`orMetadata${orMetadataIndex}`}>
                              {orMetadataIndex === 0 ? (
                                <ORTag as="div"></ORTag>
                              ) : (
                                <ORTag as="div">OR</ORTag>
                              )}
                              <span>{metadataName}</span>
                              <span>{getLabelByValue(orMetadata.op)}</span>
                              <span style={{ color: 'rgba(0, 0, 0, 0.88)', textAlign: 'left' }}>
                                {orMetadata.value}
                              </span>
                            </MappingRules>
                          );
                        })}
                      </MappingRulesContainer>
                    </div>
                  );
                })}
              </div>
            );
          });
        })}
      </RulesBuilderContainer>
    </div>
  );
};

const MappedEventDetail: React.FC<IProps> = ({ loading = false, data }) => {
  const { isLoading: isDimensionListLoading } = useGetDimensionsList();

  if (!data) return <Empty description="Data Not Found" />;

  return (
    <Container>
      <Spin spinning={loading || isDimensionListLoading}>
        <Header>
          <Icon component={MappingRowSvg} />
          <span>Mapped Event Details</span>
        </Header>
        <Body>
          <div style={{ marginBottom: '8px' }}>
            <EventNameValueWrapper>
              <Name>Mapped Event :</Name>
              <Value>{data?.event_name}</Value>
            </EventNameValueWrapper>
            <EventNameValueWrapper>
              <Name>Event Category :</Name>
              <Value>{data?.event_category}</Value>
            </EventNameValueWrapper>
            <EventNameValueWrapper>
              <Name>Description :</Name>
              <Value>{data?.description}</Value>
            </EventNameValueWrapper>
          </div>
          <MappingRulesRender rules={data?.rules} />
        </Body>
      </Spin>
    </Container>
  );
};

export default MappedEventDetail;
